import React, { useEffect, useState } from 'react';
import { changeCategoriesAPI } from '../api/client';

// Mirrors backend models.SendBackRule -- keep in step with it.
const RULE = {
  APPROVER_CHOICE: 'approver_choice',
  AUTHOR_ONLY: 'author_only',
  PREVIOUS_GATE_ONLY: 'previous_gate_only',
};

function RejectModal({ approval, onSave, onClose }) {
  const [categories, setCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [reason, setReason] = useState('');
  const [target, setTarget] = useState('draft');
  const [targetStepId, setTargetStepId] = useState(approval.prior_steps[0]?.template_step_id || '');

  const hasEarlierGate = approval.prior_steps.length > 0;

  useEffect(() => {
    // getSelectable, not getAll: change_categories is non-hierarchical, so
    // .read is org-wide-only, and a node-scoped approver -- exactly who opens
    // this modal -- is not guaranteed to hold it (issue #150/#22's trap). This
    // was the whole reason the "Lookup Reader" role existed; the endpoint is
    // the actual fix.
    changeCategoriesAPI.getSelectable()
      .then((res) => setCategories(res.data))
      .catch(() => setCategories([]))
      .finally(() => setCategoriesLoaded(true));
  }, []);

  const selectedCategory = categories.find((c) => String(c.id) === String(categoryId));
  // PREVIOUS_GATE_ONLY falls back to the author automatically at gate 1 -- the
  // backend does this too (see approvals.py::reject_step), matched here so the
  // form doesn't offer a choice the server would reject as impossible.
  const effectiveRule = selectedCategory
    ? (selectedCategory.send_back_rule === RULE.PREVIOUS_GATE_ONLY && !hasEarlierGate
        ? RULE.AUTHOR_ONLY
        : selectedCategory.send_back_rule)
    : null;

  // The category constrains the target -- move the radio to match rather than
  // leaving a stale, now-invalid selection sitting there.
  useEffect(() => {
    if (effectiveRule === RULE.AUTHOR_ONLY) setTarget('draft');
    else if (effectiveRule === RULE.PREVIOUS_GATE_ONLY) setTarget('step');
  }, [effectiveRule]);

  const draftLocked = effectiveRule === RULE.PREVIOUS_GATE_ONLY;
  const stepLocked = effectiveRule === RULE.AUTHOR_ONLY || !hasEarlierGate;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!categoryId) {
      alert('Select a category for this return');
      return;
    }
    if (!reason.trim()) {
      alert('A rejection reason is required');
      return;
    }
    if (target === 'step' && !targetStepId) {
      alert('Select which gate to send this back to');
      return;
    }

    onSave({
      reason: reason.trim(),
      change_category_id: parseInt(categoryId),
      send_back_to_draft: target === 'draft',
      send_back_to_template_step_id: target === 'step' ? parseInt(targetStepId) : null,
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Reject: {approval.document_title}</h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Category *</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={!categoriesLoaded}
            >
              <option value="">
                {categoriesLoaded ? 'Select a category…' : 'Loading…'}
              </option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {effectiveRule === RULE.AUTHOR_ONLY && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--muted)' }}>
                This category must return to the author.
              </div>
            )}
            {effectiveRule === RULE.PREVIOUS_GATE_ONLY && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--muted)' }}>
                This category must return to an earlier gate.
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Reason *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What needs to change before this can be approved?"
            />
          </div>

          <div className="form-group">
            <label>Send back to *</label>
            <div>
              <label>
                <input
                  type="radio"
                  name="target"
                  checked={target === 'draft'}
                  disabled={draftLocked}
                  onChange={() => setTarget('draft')}
                /> Author (back to Draft for revision)
              </label>
            </div>
            <div style={{ marginTop: '6px' }}>
              <label>
                <input
                  type="radio"
                  name="target"
                  checked={target === 'step'}
                  disabled={stepLocked}
                  onChange={() => setTarget('step')}
                /> An earlier gate
                {!hasEarlierGate && (
                  <span style={{ color: 'var(--muted)', marginLeft: '6px' }}>(this is the first gate)</span>
                )}
              </label>
            </div>
          </div>

          {target === 'step' && hasEarlierGate && (
            <div className="form-group">
              <label>Gate</label>
              <select value={targetStepId} onChange={(e) => setTargetStepId(e.target.value)}>
                {approval.prior_steps.map(step => (
                  <option key={step.template_step_id} value={step.template_step_id}>
                    Gate {step.step_order}: {step.group_name}
                    {step.group_owner_name ? ` (${step.group_owner_name})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-danger">
              ✗ Reject
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RejectModal;
